import { Reveal, Countdown } from "@/components/bits";
import { ArrowRight, Landmark, CreditCard, Globe } from "lucide-react";

const PAYMENT_METHODS = [
  {
    icon: Landmark,
    title: "Transferencia bancaria",
    detail: "Directa a cuenta bancaria",
    value: null,
    note: "Cuenta en pesos · Argentina",
    testid: "pago-transferencia",
  },
  {
    icon: CreditCard,
    title: "MercadoPago",
    detail: "Pagá con tarjeta, débito o saldo",
    value: null,
    note: "Hasta 12 cuotas",
    testid: "pago-mercadopago",
  },
  {
    icon: Globe,
    title: "PayPal",
    detail: "Internacional (USD)",
    value: null,
    note: "Para lectoras fuera de Argentina",
    testid: "pago-paypal",
  },
];

const INCLUDES = [
  ["7 encuentros en vivo", " de 1 hora a 1 hora y media con un círculo íntimo de lectoras"],
  ["4 libros en PDF", " con material literario elegido especialmente para cada autora"],
  ["4 guías de lectura", " con contextualización de las autoras e información extra"],
  [
    "1 devolución personalizada",
    " de un análisis hecho por vos, con los pasos exactos a seguir para afilar tu ojo crítico y agudizar tu mirada",
  ],
  [
    "Acceso al grupo de Whatsapp",
    " para continuar el diálogo y el análisis entre los encuentros, hacer actividades breves asincrónicas y recibir el material",
  ],
];

const IncludesList = () => (
  <div className="mx-auto max-w-2xl text-left" data-testid="plan-includes">
    <p className="flex items-center gap-4 text-[10px] font-medium uppercase tracking-[0.3em] text-gold">
      <span className="h-px w-8 bg-gold/50" /> Incluye <span className="h-px w-8 bg-gold/50" />
    </p>
    <ul className="mt-6 space-y-4">
      {INCLUDES.map(([lead, rest]) => (
        <li key={lead} className="flex gap-3 text-base font-light leading-relaxed text-bone/85 sm:text-lg">
          <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-crimson-light" />
          <span>
            <strong className="font-semibold text-gold">{lead}</strong>
            {rest}
          </span>
        </li>
      ))}
    </ul>
    <p className="mt-6 text-base font-light italic leading-relaxed text-gold sm:text-lg">
      Son dos meses de lectura, análisis crítico y conversación profunda entre lectoras. Solo para quienes realmente
      quieran transformar su mirada!
    </p>
  </div>
);

export default function Planes() {
  return (
    <section id="planes" data-testid="planes-section" className="bg-ink py-20 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <p className="flex items-center justify-center gap-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-10 bg-gold/50" /> Planes de pago <span className="h-px w-10 bg-gold/50" />
          </p>
          <h2 className="mt-8 font-serif text-3xl leading-[1.12] tracking-tight text-cream sm:text-4xl lg:text-5xl">
            Elegí cómo querés <span className="font-semibold not-italic text-gold">sumarte</span>
          </h2>
          <p className="mx-auto mt-6 max-w-md text-base font-light italic leading-relaxed text-gold sm:text-lg">
            Oferta especial para quienes confirmen su participación hasta el 3 de septiembre.
          </p>
        </Reveal>

        <Reveal delay={0.05} className="mt-14">
          <IncludesList />
        </Reveal>

        <Reveal delay={0.08} className="mt-12 text-center">
          <p className="mb-5 text-[10px] font-medium uppercase tracking-[0.3em] text-gold">
            La oferta termina el 3 de septiembre
          </p>
          <Countdown />
        </Reveal>

        <div className="mx-auto mt-14 grid max-w-4xl grid-cols-1 gap-8 lg:grid-cols-2">
          <Reveal>
            <div data-testid="plan-full-card" className="relative flex h-full flex-col border border-crimson-light/60 bg-ink p-10 text-center">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-crimson px-3 py-1 text-[10px] font-medium uppercase tracking-[0.25em] text-cream">
                Recomendado
              </span>
              <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold">Pago único</p>
              <h3 className="mt-4 font-serif text-3xl text-cream">Full</h3>
              <p className="mt-8 font-serif text-2xl text-bone/70 sm:text-3xl">
                <span className="line-through decoration-crimson-light decoration-2">AR$ 415.000</span>
              </p>
              <p className="mt-1 font-serif text-4xl text-cream sm:text-5xl">AR$ 340.000</p>
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-ash">USD 250</p>
              <p className="mt-6 text-sm font-light text-ash">Pago único · acceso completo.</p>
              <div className="mt-auto pt-10">
                <a
                  href="https://mpago.la/1WZTBSS"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="plan-full-button"
                  className="flex items-center justify-center gap-2 bg-crimson py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-cream transition-colors duration-300 hover:bg-crimson-light"
                >
                  Confirmar mi cupo <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div data-testid="plan-cuotas-card" className="flex h-full flex-col border border-cream/15 bg-ink p-10 text-center">
              <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold">En cuotas</p>
              <h3 className="mt-4 font-serif text-3xl text-cream">2 cuotas</h3>
              <p className="mt-8 font-serif text-2xl text-bone/70 sm:text-3xl">
                <span className="line-through decoration-crimson-light decoration-2">AR$ 220.000</span>
              </p>
              <p className="mt-1 font-serif text-4xl text-cream sm:text-5xl">AR$ 185.000</p>
              <p className="mt-6 text-sm font-light text-ash">Por cuota · durante 2 meses.</p>
              <div className="mt-auto pt-10">
                <a
                  href="https://mpago.la/2MT5Yen"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="plan-cuotas-button"
                  className="flex items-center justify-center gap-2 bg-crimson py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-cream transition-colors duration-300 hover:bg-crimson-light"
                >
                  Confirmar mi cupo <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="mx-auto mt-12 max-w-6xl">
          <div data-testid="bonus-laboratorio" className="border border-gold/40 bg-crimson/10 p-8 sm:p-10">
            <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-gold">
              Bonus · Primeras 3 lectoras que ingresan
            </p>
            <p
              data-testid="laboratorio-cupos-badge"
              className="mt-4 inline-flex items-center gap-3 border border-cream/25 bg-cream/5 px-3 py-2"
            >
              <span className="text-[10px] font-medium uppercase tracking-[0.25em] text-ash">Agotado</span>
            </p>
            <h3 className="mt-4 font-serif text-2xl tracking-tight text-cream sm:text-3xl">
              Laboratorio secreto de escritura
            </h3>
            <p className="mt-4 max-w-2xl text-sm font-light leading-relaxed text-ash sm:text-base">
              Un espacio 1:1 conmigo para experimentar con la escritura a partir de algunos de los procedimientos
              que vamos a explorar en Bestias.
            </p>
            <p className="mt-6 text-[10px] font-medium uppercase tracking-[0.25em] text-gold">Incluye</p>
            <ul className="mt-3 space-y-3">
              <li className="flex gap-3 text-sm font-light leading-relaxed text-bone">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-crimson-light" />
                Una sesión individual de experimentación y trabajo en vivo.
              </li>
              <li className="flex gap-3 text-sm font-light leading-relaxed text-bone">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-crimson-light" />
                La posibilidad de enviarme un escrito propio.
              </li>
              <li className="flex gap-3 text-sm font-light leading-relaxed text-bone">
                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-crimson-light" />
                Una devolución personalizada por escrito sobre ese texto.
              </li>
            </ul>
          </div>
        </Reveal>

        <div className="mt-28">
          <Reveal className="text-center">
            <p className="flex items-center justify-center gap-4 text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-10 bg-gold/50" /> Formas de pago <span className="h-px w-10 bg-gold/50" />
            </p>
            <h3 className="mt-8 font-serif text-2xl font-medium tracking-tight text-cream sm:text-3xl">
              ¿Cómo puedo pagar?
            </h3>
            <p className="mx-auto mt-4 max-w-xl text-sm font-light leading-relaxed text-ash">
              El pago se coordina después de la entrevista, una vez confirmado tu lugar. Elegí el método que más te
              convenga: te comparto los datos por mail o WhatsApp.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {PAYMENT_METHODS.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.1}>
                <div
                  data-testid={`${m.testid}-card`}
                  className="flex h-full flex-col border border-cream/15 bg-ink p-8 transition-colors duration-500 hover:border-gold/40"
                >
                  <m.icon size={20} className="text-gold" />
                  <h4 className="mt-5 font-serif text-xl text-cream">{m.title}</h4>
                  <p className="mt-2 text-sm font-light text-ash">{m.detail}</p>
                  {m.value && (
                    <p className="mt-4 border border-cream/20 px-3 py-2 text-center font-sans text-sm tracking-[0.15em] text-gold">
                      {m.value}
                    </p>
                  )}
                  <p className="mt-4 text-[10px] font-medium uppercase tracking-[0.22em] text-ash/70">{m.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
